import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useRef } from 'react';

import type {
    SchemaLoanRequestResolver_Get,
    SchemaLoanRequestResolver_GetOfferRequestConditionsLatest,
} from 'cpool-tables/backend-resolvers';
import { hasChanges } from 'cpool-utils';
import { useFormContext } from 'react-hook-form';

import type { getRuntimeOptions } from './RequestConfiguration/util/getRuntimeOptions';
import { prepareDefaultValues } from './utils/prepareDefaultValues.ts';
import { prepareLiabilities } from './utils/prepareLiabilities';
import { CreditCheckButton } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/CreditCheckButton.tsx';
import { DataSharing } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/DataSharing';
import { calculateSalary } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/DataSharing/utils/calculateSalary.ts';
import { LiabilitiesSummary } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/LiabilitiesSummary';
import { RefinancePlanner } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/RefinancePlanner';
import { RequestConfiguration } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/RequestConfiguration';
import { ResidualDebtInsuranceSection } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/ResidualDebtInsurance';
import type { SchemaCreditRequestForm } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/schemas/creditRequestForm.schema.ts';
import { getSalaryFromLead } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/utils/getSalaryFromLead.ts';

type Props = {
    requestConditions: SchemaLoanRequestResolver_GetOfferRequestConditionsLatest | undefined;
    currentRequestId?: string;
    setCurrentRequestId: Dispatch<SetStateAction<string | undefined>>;
    lead: SchemaLoanRequestResolver_Get;
    runtimeOptions: ReturnType<typeof getRuntimeOptions>;
};

export const RequestForm: React.FC<Props> = ({
    currentRequestId,
    lead,
    requestConditions,
    runtimeOptions,
    setCurrentRequestId,
}) => {
    const currentFirstApplicantId = useRef<string>(lead.antragsteller1.id);
    const currentSecondApplicantId = useRef<string>(lead.antragsteller2?.id ?? '');
    const currentVerbindlichkeiten = useRef<SchemaLoanRequestResolver_Get['haushalt']['verbindlichkeiten']>(
        lead.haushalt.verbindlichkeiten,
    );
    const salaryValues = useMemo(() => getSalaryFromLead(lead), [lead]);

    const methods = useFormContext<SchemaCreditRequestForm>();

    useEffect(() => {
        const salaryMethod1 = methods.getValues('dataSnippets.applicant1.salaryMode');
        const salaryMethod2 = methods.getValues('dataSnippets.applicant2.salaryMode');
        methods.setValue(
            'dataSnippets.applicant1.salary',
            calculateSalary({ applicant: 1, salaryMethod: salaryMethod1, salaryValues }),
        );
        methods.setValue(
            'dataSnippets.applicant2.salary',
            calculateSalary({ applicant: 2, salaryMethod: salaryMethod2, salaryValues }),
        );
    }, [methods, salaryValues]);

    useEffect(() => {
        if (!hasChanges({ newData: lead.haushalt.verbindlichkeiten, oldData: currentVerbindlichkeiten.current }))
            return;
        currentVerbindlichkeiten.current = lead.haushalt.verbindlichkeiten;

        const mapLiability = (
            liability:
                | SchemaCreditRequestForm['dispositionskredite'][number]
                | SchemaCreditRequestForm['geschaeftskredite'][number]
                | SchemaCreditRequestForm['kontokorrentkredite'][number]
                | SchemaCreditRequestForm['kreditkarten'][number]
                | SchemaCreditRequestForm['leasings'][number]
                | SchemaCreditRequestForm['ratenkredite'][number]
                | SchemaCreditRequestForm['sonstigeVerbindlichkeiten'][number],
        ) => ({
            hide: liability.hide,
            id: liability.id ?? '',
        });

        const currentUnorderedLiabilities: SchemaCreditRequestForm['liabilityStates'] = [
            ...methods.getValues('dispositionskredite').map(mapLiability),
            ...methods.getValues('geschaeftskredite').map(mapLiability),
            ...methods.getValues('kontokorrentkredite').map(mapLiability),
            ...methods.getValues('kreditkarten').map(mapLiability),
            ...methods.getValues('leasings').map(mapLiability),
            ...methods.getValues('ratenkredite').map(mapLiability),
            ...methods.getValues('sonstigeVerbindlichkeiten').map(mapLiability),
        ];

        Object.entries(prepareLiabilities(lead.haushalt.verbindlichkeiten, currentUnorderedLiabilities)).forEach(
            ([key, category]) => {
                // NOTE: We call `setValue` and `resetField` back-to-back on purpose: `setValue` immediately updates RHF watchers/UI (e.g. credit list),
                // NOTE: while `resetField` synchronizes the field's default state and clears stale dirty/touched/error flags after lead-driven updates.
                methods.setValue(key as keyof SchemaCreditRequestForm, category);
                methods.resetField(key as keyof SchemaCreditRequestForm, {
                    defaultValue: category,
                    keepDirty: false,
                    keepError: false,
                    keepTouched: false,
                });
            },
        );
    }, [methods, lead.haushalt.verbindlichkeiten, requestConditions?.liabilityStates]);

    useEffect(() => {
        if (
            lead.antragsteller1.id === currentSecondApplicantId.current &&
            lead.antragsteller2?.id === currentFirstApplicantId.current
        ) {
            currentFirstApplicantId.current = lead.antragsteller1.id;
            currentSecondApplicantId.current = lead.antragsteller2?.id ?? '';
            methods.reset(prepareDefaultValues({ lead, requestConditions, salaryValues }));
        }
    }, [lead, lead.antragsteller1.id, lead.antragsteller2?.id, methods, requestConditions, salaryValues]);

    return (
        <form onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-2">
            <RequestConfiguration runtimeOptions={runtimeOptions} />
            <RefinancePlanner />
            <ResidualDebtInsuranceSection
                hasSecondApplicant={!!lead.antragsteller2}
                secondApplicantHidden={!!lead.coApplicantHidden}
            />
            <DataSharing lead={lead} />
            <LiabilitiesSummary coApplicantHidden={!!lead.coApplicantHidden} />
            <CreditCheckButton lead={lead} updateRequestId={setCurrentRequestId} currentRequestId={currentRequestId} />
        </form>
    );
};
